import { Toaster } from '@navi-app/ui';
import {
  INDEXED_DB_CONFIG,
  indexedDBStorage,
  setAuthentication,
} from '@navi-app/utils';
import { login } from '@navi-app/utils/api';
import Cookies from 'js-cookie';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import companySelectionStore from '../../pages/company-selection/store/company-selection.store';
import { TVendorCompanies } from '../../pages/company-selection/types';
import config from '../constants/config';

const MAX_RETRIES = 10;
const RETRY_DELAY = 100;

export const getRedirectUrl = (url: string) => {
  const decodedUrl = decodeURIComponent(url);
  const path = decodedUrl.startsWith('/')
    ? decodedUrl.substring(1)
    : decodedUrl;

  const [domain, ...restPath] = path.split('/');
  let newPath = restPath.join('/');
  newPath = newPath !== '' ? `/${newPath}` : '';

  if (domain === 'planning') {
    return `${config.OPTIMA_URL}${newPath}`;
  } else if (domain === 'orders') {
    return `${config.MOBI_URL}${newPath}`;
  }
  // For paths without planning/orders prefix, just append to MOBI_URL
  return config.MOBI_URL;
};

const useLogin = () => {
  const { t } = useTranslation(['auth', 'error']);
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const redirectUrl = searchParams.get('redirect');
  const navigate = useNavigate();
  const { mutate, isLoading } = useMutation(login);
  const { setValues } = companySelectionStore();

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isErrorAuthenticated, setIsErrorAuthenticated] = useState(false);
  const [showErrorVerification, setShowErrorVerification] = useState(false);
  const [isErrorRateLimit, setIsErrorRateLimit] = useState(false);

  const { init } = indexedDBStorage({
    isDevelopmentEnv: false,
    storeName: INDEXED_DB_CONFIG.stores.user,
  });

  const onSubmitLogin = (values: {
    email: string;
    password: string;
    token?: string;
  }) => {
    setIsSubmitting(true);
    setIsErrorRateLimit(false);
    mutate(values, {
      onSuccess: async (data: {
        authToken: string;
        refreshToken: string;
        organizations: TVendorCompanies;
      }) => {
        // init indexedDB data
        init()
          .then(() => {
            let eligibleToAccess = 'false';
            let userRoleId = '';

            if (
              data?.organizations?.admin?.id &&
              (data?.organizations?.customers?.length ?? 0) === 0
            ) {
              eligibleToAccess = 'true';
              userRoleId = data?.organizations?.admin?.userRoleId;
            }

            setAuthentication(
              {
                authToken: data?.authToken,
                refreshToken: data?.refreshToken,
                userInitialed: 'false',
                eligibleToAccess: eligibleToAccess,
                userRoleId: userRoleId,
              },
              config?.DOMAIN_URL
            );

            setValues({
              admin: data?.organizations?.admin,
              customers: data?.organizations?.customers,
            });

            let retryCount = 0;
            setIsSubmitting(false);

            const checkAuthAndRedirect = () => {
              const authToken = Cookies.get('authToken');
              const refreshToken = Cookies.get('refreshToken');
              const userRoleId = Cookies.get('userRoleId');

              if (!authToken || !refreshToken || !userRoleId) {
                if (retryCount < MAX_RETRIES) {
                  retryCount++;
                  setTimeout(checkAuthAndRedirect, RETRY_DELAY);
                  return;
                } else {
                  window.location.replace(config.MOBI_URL);
                  return;
                }
              }

              try {
                if (redirectUrl) {
                  const finalUrl = getRedirectUrl(redirectUrl);
                  window.location.replace(finalUrl);
                } else {
                  window.location.replace(config.MOBI_URL);
                }
              } catch (error) {
                Toaster.negative(t('error:something_went_wrong'));
                window.location.replace(config.MOBI_URL);
              }
            };

            if (
              eligibleToAccess === 'false' &&
              (data?.organizations?.customers?.length ?? 0) > 0
            ) {
              if (redirectUrl) {
                navigate(
                  `/vendor/company-selection?redirect=${encodeURIComponent(
                    redirectUrl
                  )}`
                );
              } else {
                navigate('/vendor/company-selection');
              }
            } else if (type === 'newUser') {
              window.location.replace(`${config.MOBI_URL}?type=newUser`);
            } else {
              // Start checking for auth token
              checkAuthAndRedirect();
            }
          })
          .catch(() => {
            setIsSubmitting(false);
            Toaster.negative(t('warning_login_db_failed'));
          });
      },
      onError: (error) => {
        setIsSubmitting(false);
        const errorData = error as {
          response: { data: { message: string; errorCode: string } };
        };

        if (errorData?.response?.data?.message === 'ERR_USER_UNVERIFIED') {
          setShowErrorVerification(true);
        } else {
          setShowErrorVerification(false);
          setIsErrorAuthenticated(true);

          if (
            errorData?.response?.data?.errorCode === 'ERR_LOGIN_LIMIT_REACHED'
          ) {
            setIsErrorRateLimit(true);
          }
        }
      },
    });
  };

  const handShowPassword = () => setIsShowPassword(!isShowPassword);

  return {
    isSubmitting,
    isErrorAuthenticated,
    onSubmitLogin,
    setIsErrorAuthenticated,
    isLoading,
    isShowPassword,
    handShowPassword,
    showErrorVerification,
    isErrorRateLimit,
  };
};

export default useLogin;
